import { useCallback, useEffect, useState } from 'react';
import {
  getFinalCopies,
  getFinalCopyPreviewUrl,
  updateFinalCopyStatus,
} from '../services/taxFilingsService';
import type { TaxFilingDocument } from '../types/taxFilings.types';
import { DRAFT_COPY_STATUS } from '../types/taxFilings.types';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';
import { handleApiError } from '@/lib/utils/errorHandler';
import { toast } from 'sonner';

export function useFinalCopies() {
  const [documents, setDocuments] = useState<TaxFilingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const fetchDocuments = useCallback(async () => {
    if (effectiveYear == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const list = await getFinalCopies(effectiveYear);
      setDocuments(list);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveYear]);

  useEffect(() => {
    if (!configFetched || effectiveYear == null) return;
    fetchDocuments();
  }, [configFetched, effectiveYear, fetchDocuments]);

  const getPreviewUrl = useCallback(async (documentId: string): Promise<string | null> => {
    try {
      return await getFinalCopyPreviewUrl(documentId);
    } catch (err) {
      handleApiError(err);
      toast.error('Could not open preview');
      return null;
    }
  }, []);

  const approve = useCallback(
    async (documentId: string) => {
      try {
        await updateFinalCopyStatus(documentId, DRAFT_COPY_STATUS.CLIENT_VERIFIED);
        toast.success('Final copy verified');
        await fetchDocuments();
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to verify final copy');
        throw err;
      }
    },
    [fetchDocuments]
  );

  const reject = useCallback(
    async (documentId: string, rejectionComment?: string) => {
      try {
        await updateFinalCopyStatus(
          documentId,
          DRAFT_COPY_STATUS.CLIENT_REJECTED,
          rejectionComment
        );
        toast.success('Final copy rejected');
        await fetchDocuments();
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to reject final copy');
        throw err;
      }
    },
    [fetchDocuments]
  );

  return {
    documents,
    isLoading: isLoading || !configFetched,
    error,
    refresh: fetchDocuments,
    getPreviewUrl,
    approve,
    reject,
  };
}
